import type {
  PlatformFacet,
  PlatformRecommendationsRequest,
  PlatformResult,
  PlatformSearchResponse,
  PlatformSemanticQueriesRequest,
} from '@empathyco/x-adapter-platform'
import type {
  NumberRangeFacet,
  RecommendationsRequest,
  Result,
  SearchResponse,
  SemanticQueriesRequest,
} from '@empathyco/x-types'
import {
  facetSchema,
  platformAdapter,
  recommendationsRequestSchema,
  resultSchema,
  searchResponseSchema,
  semanticQueriesRequestSchema,
} from '@empathyco/x-adapter-platform'

import { getFacetConfigWithEditable } from './utils/facet.utils'

export const adapter = platformAdapter

/* Code sample about how to extend the result mapper with more fields. */

interface EmpathyDemoPlatformResult extends PlatformResult {
  description: string
  collection: string
  brand: string
  availability: string
}

declare module '@empathyco/x-types' {
  export interface Result {
    collection: string
    description: string
    brand: string
    availability: boolean
  }
}

resultSchema.$override<EmpathyDemoPlatformResult, Partial<Result>>({
  description: 'description',
  collection: 'collection',
  brand: 'brand',
  availability: ({ availability }) => availability?.toLowerCase() === 'in_stock',
  images: ({ __images }) => (Array.isArray(__images) ? __images.reverse() : [__images]),
  price: {
    value: ({ __prices }) => __prices?.current?.value,
    originalValue: ({ __prices }) => __prices?.previous?.value ?? __prices?.current.value,
    hasDiscount: ({ __prices }) =>
      __prices?.current.value < (__prices?.previous?.value ?? __prices?.current.value),
  },
})

interface MiinCosmeticsSearchResponse extends Omit<PlatformSearchResponse, 'catalog'> {
  catalog: Omit<PlatformSearchResponse['catalog'], 'stats'> & {
    stats: {
      currentPrice: {
        min: string
        max: string
      }
    }
  }
}

searchResponseSchema.$override<MiinCosmeticsSearchResponse, Partial<SearchResponse>>({
  stats: ({ catalog }) => ({
    price: {
      min: catalog.stats?.currentPrice?.min ? Number.parseFloat(catalog.stats.currentPrice.min) : 0,
      max: catalog.stats?.currentPrice?.max
        ? Number.parseFloat(catalog.stats.currentPrice.max)
        : 100,
    },
  }),
})

recommendationsRequestSchema.$override<
  RecommendationsRequest,
  Partial<PlatformRecommendationsRequest>
>({
  // TODO Top clicked demo endpoint breaks if it receives the scope parameter
  extraParams: ({ extraParams: { scope, ...extraParams } = {} }) => extraParams,
})

semanticQueriesRequestSchema.$override<
  SemanticQueriesRequest,
  Partial<PlatformSemanticQueriesRequest>
>({
  extraParams: ({ extraParams }) => {
    return {
      extraParams,
      filter_ids: 'NOT_ALL_WORDS,NOT_PARTIAL',
    }
  },
})

/**
 * Temporary override of the facet schema to customize the facet type for price facet.
 * It should be removed when the slider facet type is implemented in backend.
 */
facetSchema.$override<PlatformFacet, Partial<NumberRangeFacet>>({
  modelName: ({ type, facet }) =>
    getFacetConfigWithEditable(facet, type).modelName as 'NumberRangeFacet' | undefined,
  filters: {
    $path: 'values',
    $subSchema: ({ type, facet }) => getFacetConfigWithEditable(facet, type).schema,
  },
})
